import axios from "axios";
import {
  QueryResponse,
  Conversation,
  UsageSummary,
  UsageDetail,
  Schema,
} from "../types";
import { config } from "../config";

const api = axios.create({
  baseURL: config.API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add shared secret and subdomain to all requests
api.interceptors.request.use((req) => {
  const subdomain = localStorage.getItem("tenantSubdomain");
  if (subdomain) {
    req.headers["X-Tenant-Subdomain"] = subdomain;
  }
  req.headers["X-Shared-Secret"] = config.SHARED_SECRET;
  return req;
});

// Schema management endpoints
export const schemaAPI = {
  getSchema: async () => {
    const response = await api.get<{ success: boolean; data: Schema }>("/schema");
    return response.data.data;
  },

  getTables: async () => {
    const response = await api.get<{ success: boolean; data: any[] }>("/schema/tables");
    return response.data.data;
  },

  getTable: async (tableName: string) => {
    const response = await api.get<{ success: boolean; data: any }>(`/schema/tables/${tableName}`);
    return response.data.data;
  },

  createTable: async (table: any) => {
    const response = await api.post<{ success: boolean; data: any }>("/schema/tables", table);
    return response.data.data;
  },

  updateTable: async (tableName: string, table: any) => {
    const response = await api.put<{ success: boolean; data: any }>(`/schema/tables/${tableName}`, table);
    return response.data.data;
  },

  deleteTable: async (tableName: string) => {
    const response = await api.delete<{ success: boolean }>(`/schema/tables/${tableName}`);
    return response.data;
  },

  addColumn: async (tableName: string, column: any) => {
    const response = await api.post<{ success: boolean; data: any }>(`/schema/tables/${tableName}/columns`, column);
    return response.data.data;
  },

  updateColumn: async (tableName: string, columnName: string, column: any) => {
    const response = await api.put<{ success: boolean; data: any }>(`/schema/tables/${tableName}/columns/${columnName}`, column);
    return response.data.data;
  },

  deleteColumn: async (tableName: string, columnName: string) => {
    const response = await api.delete<{ success: boolean }>(`/schema/tables/${tableName}/columns/${columnName}`);
    return response.data;
  },
};

// Query endpoints
export const queryAPI = {
  processNaturalLanguage: async (query: string, conversationId?: string, headers?: Record<string, string>) => {
    const response = await api.post<{ success: boolean; data: QueryResponse }>(
      "/query/natural-language",
      {
        query,
        conversationId,
        options: {
          includeExplanation: true,
          maxRows: 100,
        },
      },
      { headers }
    );
    return response.data.data;
  },

  continueConversation: async (conversationId: string, query: string, headers?: Record<string, string>) => {
    const response = await api.post<{ success: boolean; data: QueryResponse }>(
      `/query/conversation/${conversationId}`,
      {
        query,
      },
      { headers }
    );
    return response.data.data;
  },

  getConversation: async (conversationId: string) => {
    const response = await api.get<{ success: boolean; data: Conversation }>(
      `/query/conversation/${conversationId}`
    );
    return response.data.data;
  },

  syncSchema: async () => {
    const response = await api.post<{ success: boolean; data: any }>(
      "/query/schema/sync"
    );
    return response.data.data;
  },

  getSchema: async () => {
    const response = await api.get<{ success: boolean; data: Schema }>(
      "/query/schema"
    );
    return response.data.data;
  },

  exportToCsv: async (query: string, conversationId?: string) => {
    const response = await api.post(
      "/query/natural-language/export-csv",
      {
        query,
        conversationId,
      },
      {
        responseType: 'blob',
      }
    );
    
    // Create a download link for the CSV file
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    link.href = url;
    link.download = `query_results_${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

// Conversation history endpoints
export const conversationAPI = {
  getAllConversations: async (params: {
    limit?: number;
    cursor?: string;
    direction?: 'next' | 'prev';
    isArchived?: boolean;
    isActive?: boolean;
    dateFrom?: string;
    dateTo?: string;
    hasErrors?: boolean;
    hasCharts?: boolean;
  } = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await api.get<{
      success: boolean;
      data: any;
    }>(`/conversations?${queryParams}`);
    return response.data.data;
  },

  searchConversations: async (params: {
    q?: string;
    dateFrom?: string;
    dateTo?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
    hasErrors?: boolean;
    hasCharts?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await api.get<{
      success: boolean;
      data: any;
    }>(`/conversations/search?${queryParams}`);
    return response.data.data;
  },

  deleteConversation: async (conversationId: string) => {
    const response = await api.delete<{ success: boolean }>(
      `/conversations/${conversationId}`
    );
    return response.data;
  },

  updateConversationTitle: async (conversationId: string, title: string) => {
    const response = await api.put<{ success: boolean; data: Conversation }>(
      `/conversations/${conversationId}`,
      { title }
    );
    return response.data.data;
  },

  continueConversation: async (conversationId: string, query: string, headers?: Record<string, string>) => {
    const response = await api.post<{ success: boolean; data: QueryResponse }>(
      `/conversations/${conversationId}/continue`,
      { query },
      { headers }
    );
    return response.data.data;
  },

  getFullConversation: async (conversationId: string) => {
    const response = await api.get<{ success: boolean; data: any }>(
      `/conversations/${conversationId}/full`
    );
    return response.data.data;
  },

  exportConversation: async (conversationId: string, format: 'markdown' | 'pdf' | 'json' | 'csv') => {
    const response = await api.get(
      `/conversations/${conversationId}/export?format=${format}`,
      {
        responseType: 'blob',
      }
    );
    
    // Create a download link for the exported file
    const mimeTypes = {
      markdown: 'text/markdown',
      pdf: 'application/pdf',
      json: 'application/json',
      csv: 'text/csv'
    };
    
    const fileExtensions = {
      markdown: 'md',
      pdf: 'pdf', 
      json: 'json',
      csv: 'csv'
    };
    
    const blob = new Blob([response.data], { type: mimeTypes[format] });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    link.href = url;
    link.download = `conversation_${conversationId}_${timestamp}.${fileExtensions[format]}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

// Admin endpoints
export const adminAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post<{ success: boolean; token: string; email: string }>(
      "/admin/login",
      { email, password }
    );
    return response.data;
  },

  verify: async (token: string) => {
    const response = await api.get<{ success: boolean; email: string }>("/admin/verify", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Usage endpoints
export const usageAPI = {
  getSummary: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const response = await api.get<{ success: boolean; data: UsageSummary }>(
      `/usage/summary?${params}`
    );
    return response.data.data;
  },

  getConversationUsage: async (conversationId: string) => {
    const response = await api.get<{ success: boolean; data: any }>(
      `/usage/conversations/${conversationId}`
    );
    return response.data.data;
  },

  getDetailedUsage: async (params: {
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    conversationId?: string;
    queryType?: string;
    tenantId?: string;
  }) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await api.get<{
      success: boolean;
      data: { data: UsageDetail[]; total: number; page: number; pages: number };
    }>(`/usage/detailed?${queryParams}`);
    return response.data.data;
  },

  getTenantGroupedUsage: async (params: {
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await api.get<{ success: boolean; data: any }>(
      `/usage/tenants?${queryParams}`
    );
    return response.data.data;
  },
};
